import React, { memo } from 'react';

import moment from 'moment';

import { isImg, isVideo } from '@shared/helpers';

import cls from './ConstructionProgressDnD.module.css';
import { EmptyConstructionProgress } from './EmptyConstructionProgress/EmptyConstructionProgress';
import { useConstructionProgressDnD } from './useConstructionProgressDnD';
import { DnD } from '../../../../../components/newUI/DnD/DnD';
import { PrevMedia } from '../../../../../components/newUI/PrevMedia/PrevMedia';
import { ProgressCard } from '../ProgressCard/ProgressCard';

interface ConstructionProgressDnDProps {
    classes?: string;
}

export const ConstructionProgressDnD = memo(({ classes }: ConstructionProgressDnDProps) => {
    const {
        onImageChange,
        mediaIds,
        is_customer,
        is_owner,
        progress_tariff_access_edit,
        previewMediaData,
        onShowPrevMedia,
        setCurrentUrl,
        setCurrentIndex,
        setCurrentMimeType,
        onHideModal,
        progress,
        prevMediaData,
        onEdit,
        uploadingMediaData,
        uploadedTempMediaData,
        isUploadQueueStarted,
        abortUploadMediaItem,
        onRetryUpload,
    } = useConstructionProgressDnD();

    const hasActiveUploads = uploadingMediaData.some((mediaItem) => mediaItem.isUploading);
    const hasUploadErrors = uploadingMediaData.some((mediaItem) => Boolean(mediaItem.errorText));
    const shouldRenderUploadCards = isUploadQueueStarted || hasUploadErrors || hasActiveUploads;

    const isVideoFile = isVideo(prevMediaData.mimeType);
    const isImgFile = isImg(prevMediaData.mimeType);

    return (
        <DnD
            addImageTitle={i18next.t('ConstructionProgressDnD.return.addMediaTitle')}
            onImageChange={onImageChange}
            isClickDnd={mediaIds.length === 0}
            isDisabled={is_customer}
            isNotDnD={is_customer}
        >
            {!!prevMediaData.currentUrl && (
                <PrevMedia
                    currentIndex={prevMediaData.currentIndex}
                    mediaDataList={previewMediaData}
                    setCurrentIndex={setCurrentIndex}
                    setCurrentUrl={setCurrentUrl}
                    setCurrentMimeType={setCurrentMimeType}
                    onHideModal={onHideModal}
                    currentUrl={prevMediaData.currentUrl}
                    fileName=""
                    title={moment(prevMediaData.date).format(
                        i18next.t('ConstructionProgressDnD.prevImage.title.format')
                    )}
                    description={prevMediaData.description}
                    isImage={isImgFile}
                    isVideoFile={isVideoFile}
                    isNotSaving
                />
            )}
            {mediaIds.length ||
            (shouldRenderUploadCards && uploadingMediaData.length) ||
            uploadedTempMediaData.length ? (
                <div className={cls.list}>
                    {shouldRenderUploadCards &&
                        uploadingMediaData.map((mediaItem, index) => (
                            <ProgressCard
                                id={`uploading-${mediaItem.id}`}
                                key={`uploading-${mediaItem.id}`}
                                index={mediaIds.length + index}
                                isUploadPlaceholder
                                isUploading={mediaItem.isUploading}
                                errorText={mediaItem.errorText}
                                previewUrl={mediaItem.previewUrl}
                                mimeType={mediaItem.mimeType}
                                onRetryUpload={() => onRetryUpload(mediaItem.id)}
                                onCancelUpload={() => abortUploadMediaItem(mediaItem.id)}
                            />
                        ))}
                    {uploadedTempMediaData.map((mediaItem, index) => (
                        <ProgressCard
                            id={`uploaded-temp-${mediaItem.id}`}
                            key={`uploaded-temp-${mediaItem.id}`}
                            index={index}
                            isUploading={false}
                            isTemporary
                            previewUrl={mediaItem.previewUrl}
                            mimeType={mediaItem.mimeType}
                            mediaUrl={mediaItem.mediaUrl}
                            date={mediaItem.date}
                            description={mediaItem.description}
                            onShowPrevMedia={onShowPrevMedia}
                        />
                    ))}
                    {mediaIds.map((id, index) => (
                        <ProgressCard
                            id={`${id}`}
                            key={id}
                            index={uploadedTempMediaData.length + index}
                            onShowPrevMedia={onShowPrevMedia}
                            isUploading={false}
                        />
                    ))}
                </div>
            ) : (
                <EmptyConstructionProgress
                    is_owner={is_owner}
                    is_customer={is_customer}
                    progress_tariff_access_edit={progress_tariff_access_edit}
                />
            )}
        </DnD>
    );
});
